import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Loader2, AlertCircle, Wifi, WifiOff } from 'lucide-react';

// Loading Animations
const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

// Loading Components
const LoadingContainer = styled.div<{ fullScreen?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing[4]};
  padding: ${({ theme }) => theme.spacing[8]};
  
  ${({ fullScreen }) => fullScreen && `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(4px);
    z-index: 9999;
  `}
`;

const SpinnerIcon = styled(Loader2)`
  width: 32px;
  height: 32px;
  color: ${({ theme }) => theme.colors.primary[600]};
  animation: ${spin} 1s linear infinite;
`;

const LoadingText = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  margin: 0;
  text-align: center;
`;

const LoadingDots = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const Dot = styled.div<{ delay: number }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.primary[600]};
  animation: ${pulse} 1.4s ease-in-out infinite;
  animation-delay: ${({ delay }) => delay}s;
`;

// Skeleton Loading Components
const SkeletonBase = styled.div`
  background: linear-gradient(90deg, 
    ${({ theme }) => theme.colors.gray[200]} 0%, 
    ${({ theme }) => theme.colors.gray[100]} 50%, 
    ${({ theme }) => theme.colors.gray[200]} 100%
  );
  background-size: 200px 100%;
  animation: ${shimmer} 1.2s ease-in-out infinite;
  border-radius: ${({ theme }) => theme.borderRadius.md};
`;

const SkeletonText = styled(SkeletonBase)<{ width?: string; height?: string }>`
  height: ${({ height }) => height || '1rem'};
  width: ${({ width }) => width || '100%'};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

const SkeletonCardBase = styled(SkeletonBase)`
  height: 200px;
  width: 100%;
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const SkeletonAvatarBase = styled(SkeletonBase)<{ size?: string }>`
  width: ${({ size }) => size || '40px'};
  height: ${({ size }) => size || '40px'};
  border-radius: 50%;
`;

// Error State Components
const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing[4]};
  padding: ${({ theme }) => theme.spacing[8]};
  text-align: center;
`;

const ErrorIcon = styled(AlertCircle)`
  width: 48px;
  height: 48px;
  color: ${({ theme }) => theme.colors.error[500]};
`;

const ErrorTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

const ErrorMessage = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
  max-width: 400px;
`;

const RetryButton = styled.button`
  background-color: ${({ theme }) => theme.colors.primary[600]};
  color: ${({ theme }) => theme.colors.text.inverse};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[4]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    background-color: ${({ theme }) => theme.colors.primary[700]};
  }

  &:disabled {
    background-color: ${({ theme }) => theme.colors.gray[400]};
    cursor: not-allowed;
  }
`;

// Connection Status Components
const ConnectionStatus = styled.div<{ isOnline: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[4]};
  background-color: ${({ isOnline, theme }) => 
    isOnline ? theme.colors.success[50] : theme.colors.error[50]
  };
  border: 1px solid ${({ isOnline, theme }) => 
    isOnline ? theme.colors.success[200] : theme.colors.error[200]
  };
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ isOnline, theme }) => 
    isOnline ? theme.colors.success[700] : theme.colors.error[700]
  };
`;

// Component Interfaces
export interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  text?: string;
  fullScreen?: boolean;
}

export interface SkeletonProps {
  lines?: number;
  width?: string;
  height?: string;
}

export interface ErrorStateProps {
  title?: string;
  message?: string;
  onRetry?: () => void;
  retryText?: string;
  isRetrying?: boolean;
}

// Main Components
export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'md',
  text,
  fullScreen = false,
}) => {
  const iconSize = size === 'sm' ? 16 : size === 'lg' ? 48 : 32;
  
  return (
    <LoadingContainer fullScreen={fullScreen}>
      <SpinnerIcon style={{ width: iconSize, height: iconSize }} />
      {text && <LoadingText>{text}</LoadingText>}
      <LoadingDots>
        <Dot delay={0} />
        <Dot delay={0.2} />
        <Dot delay={0.4} />
      </LoadingDots>
    </LoadingContainer>
  );
};

export const SkeletonLoader: React.FC<SkeletonProps> = ({
  lines = 3,
  width,
  height,
}) => {
  return (
    <div>
      {Array.from({ length: lines }).map((_, index) => (
        <SkeletonText
          key={index}
          width={index === lines - 1 ? '60%' : width}
          height={height}
        />
      ))}
    </div>
  );
};

export const SkeletonCard: React.FC = () => <SkeletonCardBase />;

export const SkeletonAvatar: React.FC<{ size?: string }> = ({ size }) => (
  <SkeletonAvatarBase size={size} />
);

export const ErrorState: React.FC<ErrorStateProps> = ({
  title = 'Something went wrong',
  message = 'We encountered an error while loading this content. Please try again.',
  onRetry,
  retryText = 'Try Again',
  isRetrying = false,
}) => {
  return (
    <ErrorContainer>
      <ErrorIcon />
      <ErrorTitle>{title}</ErrorTitle>
      <ErrorMessage>{message}</ErrorMessage>
      {onRetry && (
        <RetryButton onClick={onRetry} disabled={isRetrying}>
          {isRetrying ? 'Retrying...' : retryText}
        </RetryButton>
      )}
    </ErrorContainer>
  );
};

export const NetworkStatus: React.FC<{ isOnline: boolean }> = ({ isOnline }) => {
  return (
    <ConnectionStatus isOnline={isOnline}>
      {isOnline ? <Wifi size={16} /> : <WifiOff size={16} />}
      {isOnline ? 'Connected' : 'No internet connection'}
    </ConnectionStatus>
  );
};

// Page Loading States
export const PageLoading: React.FC<{ text?: string }> = ({ text = 'Loading page...' }) => (
  <LoadingSpinner text={text} fullScreen />
);

export const ContentLoading: React.FC<{ text?: string }> = ({ text = 'Loading content...' }) => (
  <LoadingSpinner text={text} />
);

// Empty States
const EmptyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing[4]};
  padding: ${({ theme }) => theme.spacing[12]} ${({ theme }) => theme.spacing[8]};
  text-align: center;
`;

const EmptyTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

const EmptyMessage = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
  max-width: 400px;
`;

export interface EmptyStateProps {
  title: string;
  message: string;
  action?: React.ReactNode;
  icon?: React.ReactNode;
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  title,
  message,
  action,
  icon,
}) => {
  return (
    <EmptyContainer>
      {icon}
      <EmptyTitle>{title}</EmptyTitle>
      <EmptyMessage>{message}</EmptyMessage>
      {action}
    </EmptyContainer>
  );
};